import { useState } from 'react';
import type { Recommendation } from '../../types/analysis.types';
import { PriorityBadge } from './PriorityBadge';
import { Check, ChevronDown } from 'lucide-react';

interface SelectableRecommendationCardProps {
  recommendation: Recommendation;
  isSelected: boolean;
  onToggle: (id: string) => void;
  variant?: 'compact' | 'expanded';
}

export const SelectableRecommendationCard: React.FC<SelectableRecommendationCardProps> = ({
  recommendation,
  isSelected,
  onToggle,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on the expand button
    if ((e.target as HTMLElement).closest('.expand-button')) {
      return;
    }
    onToggle(recommendation.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault();
      onToggle(recommendation.id);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setIsExpanded((prev) => !prev);
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const priorityColors = {
    Critical: {
      border: 'border-rose-500',
      selectedBorder: 'border-rose-400',
      glow: 'shadow-rose-500/50',
      lightBg: 'bg-rose-50',
      lightBorder: 'border-rose-300',
    },
    High: {
      border: 'border-orange-500',
      selectedBorder: 'border-orange-400',
      glow: 'shadow-orange-500/50',
      lightBg: 'bg-orange-50',
      lightBorder: 'border-orange-300',
    },
    Medium: {
      border: 'border-amber-500',
      selectedBorder: 'border-amber-400',
      glow: 'shadow-amber-500/50',
      lightBg: 'bg-amber-50',
      lightBorder: 'border-amber-300',
    },
  };

  const colors = priorityColors[recommendation.priority];

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg p-5 transition-all duration-300 cursor-pointer border-2 ${
        isSelected
          ? `${colors.selectedBorder} shadow-lg ${colors.glow} scale-[1.02] dark:${colors.lightBg}/0 ${colors.lightBg}`
          : `border-teal-200 dark:border-transparent hover:${colors.border} hover:shadow-md`
      }`}
      onClick={handleCardClick}
      role="checkbox"
      tabIndex={0}
      aria-checked={isSelected}
      aria-label={`${isSelected ? 'Deselect' : 'Select'} ${recommendation.title}`}
      onKeyDown={handleKeyDown}
    >
      {/* Header Row with Checkbox */}
      <div className="flex items-start gap-3 mb-3">
        {/* Checkbox */}
        <div className="shrink-0 mt-1">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
              isSelected
                ? 'bg-teal-500 border-teal-500 shadow-md shadow-teal-500/50'
                : 'border-slate-400 dark:border-slate-600 bg-white dark:bg-transparent'
            }`}
            aria-hidden="true"
          >
            {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
          </div>
        </div>

        {/* Priority Badge */}
        <div className="shrink-0">
          <PriorityBadge priority={recommendation.priority} />
        </div>

        {/* Title */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-slate-900 dark:text-slate-50 mb-1">
            {recommendation.title}
          </h3>
        </div>
      </div>

      {/* Metadata Row */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs ml-8">
        <span className="border border-cyan-700 px-3 py-1 rounded-xl text-white font-small shadow-xs flex items-center gap-1">
          {recommendation.category}
        </span>
        <span className="text-slate-500 dark:text-slate-400">•</span>
        <span className="text-slate-700 dark:text-slate-300 font-small flex items-center gap-1">
          developer time saved&nbsp;
          {recommendation.estimatedTime}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-slate-700 dark:text-slate-300 mb-3 leading-relaxed ml-8">
        {recommendation.description}
      </p>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-slate-700 space-y-4 animate-fade-in ml-8">
          {/* Impact Section */}
          <div>
            <h4 className="font-semibold text-sm mb-2 text-slate-900 dark:text-slate-50">
              Impact:
            </h4>
            <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
              {recommendation.impact}
            </p>
          </div>

          {/* Implementation Steps */}
          <div>
            <h4 className="font-semibold text-sm mb-2 text-slate-900 dark:text-slate-50">
              Implementation Steps:
            </h4>
            <ol className="space-y-2">
              {recommendation.steps.map((step, index) => (
                <li key={index} className="flex gap-3 text-sm text-slate-700 dark:text-slate-300">
                  <span className="shrink-0 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-xs shadow-md">
                    {index + 1}
                  </span>
                  <span className="flex-1 pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* Toggle Hint */}
      <div
        className="expand-button mt-3 text-xs text-slate-600 dark:text-slate-400 text-right flex items-center justify-end gap-1 hover:text-slate-900 dark:hover:text-slate-300 transition-colors"
        onClick={handleExpandClick}
        role="button"
        tabIndex={-1}
      >
        <span>{isExpanded ? 'Hide details' : 'Show details'}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </div>
    </div>
  );
};
